import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type ChatbotInstructions,
  type IAgentHandoffNotifySettings,
  type IChatbotField,
} from 'entities/Chatbot'
import { IntegrationKey } from 'entities/Integrations'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import {
  createActionChip,
  createAddContactNoteAction,
  createAddTagAction,
  createRemoveTagAction,
  createSendSMSAction,
  stopConversationAction,
  type IAgentAction,
  type IAgentActionKey,
  type ITagActionItem,
  createHandoffToHumanAction,
} from 'entities/AIAgent/lib/agentActions'
import { TagsControl } from 'entities/Tags/store/tagsControl'
import {
  EmojiAction,
  mergeFieldReplacer,
  MessageFieldStore,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { EnumVariantMessageField } from 'widgets/MessageField/store/types'
import { AutoControlStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/autoControlStore'
import { TagAction } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/TagAction/TagAction'
import { AutoContentAction } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/AutoContentAction/AutoContentAction'
import { ModalActions } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/ModalActions/ModalActions'
import { InstructionsSectionContent } from '../InstructionsSectionContent'
import styles from '../styles.module.scss'

const makeMessageFieldStoreProps = (
  onFocus: () => void,
  styles: { minHeight: number; maxHeight: number }
) => ({
  placeholder: "Set your agent's role, response style, and add any specific instructions.",
  noCounter: true,
  styles: { ...styles },
  variant: EnumVariantMessageField.Clean,
  disableMergeFieldsFallback: true,
  resetOnUnmount: false,
  makeEvents: () => ({ onFocus }),
})

export class InstructionsSectionStore {
  error = ''
  private _disposeSync: IReactionDisposer | null = null
  readonly messageFieldStore: MessageFieldStore

  constructor(
    readonly entities: IEntitiesStore,
    private _agentId: number | null,
    private _instructions: ChatbotInstructions,
    styles = { minHeight: 200, maxHeight: 408 },
    private readonly _requestEnhanceOverride: (
      prompt: string,
      signal?: AbortSignal
    ) => Promise<string>
  ) {
    this.messageFieldStore = new MessageFieldStore(
      makeMessageFieldStoreProps(this.clearError, styles),
      entities
    )

    this.messageFieldStore.setMessageData({
      message: _instructions.instructions,
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
    })

    makeAutoObservable(this)
    this.reactionSetInstructions()
  }

  reactionSetInstructions = () => {
    this._disposeSync?.()
    this._disposeSync = reaction(
      () => this.messageFieldStore.text,
      (text) => this._instructions.setInstructions(text)
    )
  }

  get instructions() {
    return this.messageFieldStore.text
  }

  private _extractMergeFieldsFromText(source: string) {
    return [...source.matchAll(/\{([^}]*)}/g)].reduce<Map<string, IChatbotField>>(
      (state, [template, key]) => {
        const fieldData = this.entities.mergeFieldStore.getMergeFieldInfoByTemplate(template)
        key = key.trim()
        const field = fieldData.mergeField
        if (field) state.set(key, { key, name: field.label, type: field.typeChatbotField })
        return state
      },
      new Map()
    )
  }

  get existMergeFields() {
    const text = this.messageFieldStore.text
    const fields = this._extractMergeFieldsFromText(text)

    for (const [, content] of text.matchAll(/"content":"((?:[^"\\]|\\.)*)"/g)) {
      for (const [key, field] of this._extractMergeFieldsFromText(content)) {
        fields.set(key, field)
      }
    }

    return fields
  }

  selectedContactFields: IChatbotField[] = []

  setContactFields = (fields: IChatbotField[]) => {
    this.selectedContactFields = fields
  }

  mergeContactFields = (fields: IChatbotField[]) => {
    this.selectedContactFields = [...this.selectedContactFields, ...fields]
  }

  get existMergeFieldsPayload() {
    const merged = new Map(this.existMergeFields)
    for (const field of this.selectedContactFields) {
      merged.set(field.key, field)
    }
    return Array.from(merged.values())
  }

  get mergeFieldConfig() {
    const hasCalendlyFields = !!this.entities.mergeFieldStore.getMergeFieldsIntegration(
      IntegrationKey.calendly
    )?.size

    return {
      isAllIntegration: true,
      includeIntegrations: hasCalendlyFields
        ? [IntegrationKey.calendly, IntegrationKey.hubspot]
        : [IntegrationKey.hubspot],
      excludeFieldsIntegrations: {
        [IntegrationKey.salesmessage]: ['number'],
      },
    }
  }

  get requestEnhance() {
    return this._requestEnhanceOverride
  }

  clearError = () => {
    this.error = ''
  }

  setError = (value: string) => {
    this.error = value
  }

  setInstructions = (value: string) => {
    this.messageFieldStore.setMessageData({
      message: value,
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
    })
    this.error = ''
  }

  onAddMergeField = (field: MergeField, replaceContent?: string) => {
    if (!this.messageFieldStore.addContent) return
    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'

    this.messageFieldStore.addContent(fieldContent, replaceContent)
  }

  onAddAction = (action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.addContent(createActionChip(action) + '&nbsp;', '/')
  }
  replaceAction = (chip: HTMLSpanElement, action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.replaceActionChip(chip, createActionChip(action))
  }
  deleteAction = (chip: HTMLSpanElement) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.removeActionChip(chip)
  }

  private _openTagModal = (
    isAddTag: boolean,
    initialIds: number[],
    onSave: (tagsControl: TagsControl, modalId: string) => void,
    onDelete?: () => void
  ) => {
    const modalId = nanoid()
    const tagsControl = new TagsControl(initialIds)

    modalStore.addModal({
      id: modalId,
      title: isAddTag ? 'Add tag' : 'Remove tag',
      width: 480,
      showCloseButton: false,
      onClose: () => modalStore.removeModal(modalId),
      ModalContent: TagAction,
      ModalActions: ModalActions,
      ModalContentProps: {
        control: tagsControl,
        canCreate: isAddTag,
        onSave: () => onSave(tagsControl, modalId),
        onCancel: () => modalStore.removeModal(modalId),
        onDelete: onDelete
          ? () => {
              modalStore.removeModal(modalId)
              onDelete()
            }
          : undefined,
        isSaveDisabled: () => tagsControl.ids.length === 0,
      },
      className: styles.tagModal,
    })
  }

  private _openSmsModal = (
    initialData: { auto?: boolean; content?: string },
    onSave: (control: AutoControlStore, contentStore: MessageFieldStore, modalId: string) => void,
    onDelete?: () => void
  ) => {
    const modalId = nanoid()
    const control = new AutoControlStore(initialData)
    const contentStore = new MessageFieldStore(
      {
        placeholder: 'Write your message...',
        textLimit: { maxLength: 160, message: 'SMS can contain max 160 characters' },
        styles: { minHeight: 102 },
        makeActions: () => [
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => {
                  if (!contentStore.addContent) return
                  contentStore.addContent(
                    mergeFieldReplacer(this.entities, value.template) + '&nbsp;'
                  )
                }}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
      },
      this.entities
    )

    contentStore.setMessageData({
      message: initialData.content ?? '',
      attachments: [],
      isReset: false,
      isReplace: true,
      isFocus: true,
    })

    modalStore.addModal({
      id: modalId,
      title: 'Send SMS',
      width: 480,
      showCloseButton: false,
      onClose: () => modalStore.removeModal(modalId),
      ModalContent: AutoContentAction,
      ModalActions: ModalActions,
      ModalContentProps: {
        control,
        contentStore,
        title: 'Let AI write this message',
        questionInfoTooltip: 'Agent will write this message based on the conversation',
        onSave: () => onSave(control, contentStore, modalId),
        onCancel: () => modalStore.removeModal(modalId),
        onDelete: onDelete
          ? () => {
              control.setIsAuto(false)
              modalStore.removeModal(modalId)
              onDelete()
            }
          : undefined,
        isSaveDisabled: () => !control.isAuto && !contentStore.text.trim(),
      },
    })
  }

  private _openNoteModal = (
    initialData: { auto?: boolean; content?: string },
    onSave: (control: AutoControlStore, noteStore: MessageFieldStore, modalId: string) => void,
    onDelete?: () => void
  ) => {
    const modalId = nanoid()
    const control = new AutoControlStore(initialData)
    const noteStore = new MessageFieldStore(
      {
        placeholder: 'Write a note...',
        noCounter: true,
        variant: EnumVariantMessageField.Note,
        textLimit: { maxLength: 255, message: 'Note can contain max 255 characters' },
        makeActions: () => [{ iconButtonComponent: <EmojiAction /> }],
      },
      this.entities
    )

    noteStore.setMessageData({
      message: initialData.content ?? '',
      attachments: [],
      isReset: false,
      isReplace: true,
      isFocus: true,
    })

    modalStore.addModal({
      id: modalId,
      title: 'Add contact note',
      width: 480,
      showCloseButton: false,
      onClose: () => modalStore.removeModal(modalId),
      ModalContent: AutoContentAction,
      ModalActions: ModalActions,
      ModalContentProps: {
        control,
        contentStore: noteStore,
        title: 'Let AI write this note',
        questionInfoTooltip: 'Agent will write this note based on the conversation',
        onSave: () => onSave(control, noteStore, modalId),
        onCancel: () => modalStore.removeModal(modalId),
        onDelete: onDelete
          ? () => {
              control.setIsAuto(false)
              modalStore.removeModal(modalId)
              onDelete()
            }
          : undefined,
        isSaveDisabled: () => !control.isAuto && !noteStore.text.trim(),
      },
    })
  }

  onActionItemClick = (key: IAgentActionKey, onShortcutClose: () => void) => {
    onShortcutClose()

    if (key === 'stopConversation') {
      this.onAddAction(stopConversationAction)
      return
    }

    if (key === 'addTag' || key === 'removeTag') {
      this._openTagModal(key === 'addTag', [], (tagsControl, modalId) => {
        if (tagsControl.ids.length > 0) {
          const tags = tagsControl.tags.map(
            (tag): ITagActionItem => ({ id: tag.id, name: tag.name })
          )
          const action =
            key === 'addTag' ? createAddTagAction({ tags }) : createRemoveTagAction({ tags })
          this.onAddAction(action)
        }
        modalStore.removeModal(modalId)
      })
    }

    if (key === 'addNote') {
      this._openNoteModal({}, (control, noteStore, modalId) => {
        const content = noteStore.text
        const data = control.isAuto ? { auto: true as const } : { content }
        if (control.isAuto || content.trim()) {
          this.onAddAction(createAddContactNoteAction(data))
        }
        modalStore.removeModal(modalId)
      })
    }

    if (key === 'sendSMS') {
      this._openSmsModal({}, (control, contentStore, modalId) => {
        const content = contentStore.text
        const data = control.isAuto ? { auto: true as const } : { content }
        if (control.isAuto || content.trim()) {
          this.onAddAction(createSendSMSAction(data))
        }
        modalStore.removeModal(modalId)
      })
    }
  }

  private _replaceChip = (chipElement: HTMLSpanElement, newAction: IAgentAction | null) => {
    this.messageFieldStore.replaceActionChip(
      chipElement,
      newAction ? createActionChip(newAction) : null
    )
  }

  onActionChipClick = (chipElement: HTMLSpanElement) => {
    const template = chipElement.dataset.template || ''

    const tagMatch = template.match(/^@(addTag|removeTag)\((.+)\)$/)
    if (tagMatch) {
      const isAddTag = tagMatch[1] === 'addTag'
      let initialIds: number[] = []

      try {
        initialIds = (JSON.parse(tagMatch[2]) as { tags: ITagActionItem[] }).tags.map(
          (tag) => tag.id
        )
      } catch {
        initialIds = []
      }

      this._openTagModal(
        isAddTag,
        initialIds,
        (tagsControl, modalId) => {
          const tags = tagsControl.tags.map(
            (tag): ITagActionItem => ({ id: tag.id, name: tag.name })
          )
          const newAction = isAddTag
            ? createAddTagAction({ tags })
            : createRemoveTagAction({ tags })
          this._replaceChip(chipElement, newAction)
          modalStore.removeModal(modalId)
        },
        () => this._replaceChip(chipElement, null)
      )
      return
    }

    const noteMatch = template.match(/^@addNote\((.+)\)$/)
    if (noteMatch) {
      let initialData = {}

      try {
        initialData = JSON.parse(noteMatch[1])
      } catch {
        initialData = {}
      }

      this._openNoteModal(
        initialData,
        (control, noteStore, modalId) => {
          const content = noteStore.text
          const newAction = createAddContactNoteAction(
            control.isAuto ? { auto: true } : { content }
          )
          this._replaceChip(chipElement, newAction)
          modalStore.removeModal(modalId)
        },
        () => this._replaceChip(chipElement, null)
      )
      return
    }

    const smsMatch = template.match(/^@sendSMS\((.+)\)$/)
    if (smsMatch) {
      let initialData = {}

      try {
        initialData = JSON.parse(smsMatch[1])
      } catch {
        initialData = {}
      }

      this._openSmsModal(
        initialData,
        (control, contentStore, modalId) => {
          const content = contentStore.text
          const newAction = createSendSMSAction(control.isAuto ? { auto: true } : { content })
          this._replaceChip(chipElement, newAction)
          modalStore.removeModal(modalId)
        },
        () => this._replaceChip(chipElement, null)
      )
    }
  }

  openModal = () => {
    const modalId = nanoid()
    const snapshot = this._instructions.instructions

    const modalContentStore = new InstructionsSectionStore(
      this.entities,
      this._agentId,
      this._instructions,
      { minHeight: 452, maxHeight: 452 },
      this._requestEnhanceOverride
    )

    const handleCancel = () => {
      modalContentStore.dispose()
      this._instructions.setInstructions(snapshot)
      this.setInstructions(snapshot)
      modalStore.removeModal(modalId)
    }

    const handleSave = () => {
      modalContentStore.dispose()
      this.setInstructions(modalContentStore.instructions)
      modalStore.removeModal(modalId)
    }

    modalStore.addModal({
      id: modalId,
      title: 'Edit',
      width: 800,
      paddingContent: '0 24px 8px',
      onClose: handleCancel,
      primaryAction: {
        text: 'Save',
        onAction: handleSave,
      },
      ModalContent: InstructionsSectionContent,
      ModalContentProps: {
        store: modalContentStore,
        isExpanded: true,
      },
    })
  }

  dispose = () => {
    this._disposeSync?.()
  }

  onAddHandoffToHumanAction = (data: IAgentHandoffNotifySettings) => {
    this.onAddAction(createHandoffToHumanAction(data))
  }
  onUpdateHandoffToHumanAction = (chip: HTMLSpanElement, data: IAgentHandoffNotifySettings) => {
    this.replaceAction(chip, createHandoffToHumanAction(data))
  }
}
